import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import backend from '@api/backend'
import {
  notifyApiError,
  notifySuccess,
} from '@common/utils/notify'
import { emptyToNull } from '@common/utils/string'
import Account from '@data/accounts/model/account'
import CustomerLocation from '@data/locations/model/customerLocation'
import {
  LOCATIONS_QUERY_KEY,
  LocationApiResponse,
  mapLocationResponse,
} from './locations'

export const ACCOUNT_LOCATIONS_QUERY_KEY = ['account-locations'] as const

interface AccountLocationResponse extends LocationApiResponse {
  billingEntityId?: number | null
  stripeRecurringPriceId?: string | null
}

const mapAccountLocationResponse = (location: AccountLocationResponse): CustomerLocation => ({
  ...mapLocationResponse(location),
  billingEntityId: location.billingEntityId ?? null,
  stripeRecurringPriceId: location.stripeRecurringPriceId ?? null,
})

const accountLocationsQueryKey = (accountId: string) =>
  [...ACCOUNT_LOCATIONS_QUERY_KEY, accountId] as const

const invalidateLocations = async (
  queryClient: ReturnType<typeof useQueryClient>,
  accountId: string,
) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: accountLocationsQueryKey(accountId),
      exact: true,
    }),
    queryClient.invalidateQueries({ queryKey: LOCATIONS_QUERY_KEY }),
  ])
}

export const useAccountLocationsQuery = (accountId: string | null, enabled = true) =>
  useQuery<CustomerLocation[]>({
    queryKey: accountLocationsQueryKey(accountId ?? ''),
    queryFn: async ({ signal }) => {
      const response = await backend.get<AccountLocationResponse[]>(
        `/accounts/${accountId}/customer_locations`,
        { signal },
      )
      return response.data
        .map(mapAccountLocationResponse)
        .sort((a, b) => a.companyAlias.localeCompare(b.companyAlias) || a.name.localeCompare(b.name))
    },
    enabled: enabled && accountId !== null,
    staleTime: 0,
    refetchOnMount: 'always',
  })

const buildLocationRequest = (location: CustomerLocation, account: Account) => ({
  addressLine1: location.addressLine1,
  addressLine2: emptyToNull(location.addressLine2),
  town: emptyToNull(location.town),
  county: emptyToNull(location.county),
  postcode: emptyToNull(location.postcode),
  country: location.country,
  locationName: location.name,
  accountIdRef: Number(account.id),
  companyAlias: emptyToNull(location.companyAlias),
  active: location.active,
  settings: {
    onboardingStatus: location.settings.onboardingStatus,
    onboardingDate: location.settings.onboardingDate,
  },
})

export const useSaveAccountLocationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ location, account }: { location: CustomerLocation; account: Account }) => {
      const request = buildLocationRequest(location, account)

      if (!location.id) {
        return backend.post('/customer_locations', request)
      }

      return backend.put(`/customer_locations/${location.id}`, {
        ...request,
        active: location.active,
        suspended: location.suspended,
      })
    },
    onSuccess: async (_response, { location, account }) => {
      await invalidateLocations(queryClient, account.id)
      notifySuccess(
        location.id ? 'Location updated successfully' : 'Location created successfully',
      )
    },
    onError: (error: unknown) => {
      notifyApiError(error)
    },
  })
}

export const useRemoveAccountLocationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ location, account }: { location: CustomerLocation; account: Account }) => {
      await backend.delete(`/customer_locations/${location.id}`)
      return account
    },
    onSuccess: async (account) => {
      await invalidateLocations(queryClient, account.id)
      notifySuccess('Location deleted successfully')
    },
    onError: (error: unknown) => {
      notifyApiError(error)
    },
  })
}
